import React, { useState } from 'react';
import Button from '../components/ui/Button';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import {
  FaBed, FaRulerCombined, FaMountain, FaSmokingBan, FaBath, 
  FaConciergeBell, FaFan, FaCoffee, FaSnowflake, FaLock, 
  FaTshirt, FaPlug, FaMosquito
} from 'react-icons/fa';

const { FiCheck, FiWifi, FiChevronLeft, FiChevronRight } = FiIcons;

const RoomCard = ({ room }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const nextImage = (e) => {
    e?.stopPropagation();
    setCurrentImageIndex((prev) => (prev === room.images.length - 1 ? 0 : prev + 1));
  };

  const prevImage = (e) => {
    e?.stopPropagation();
    setCurrentImageIndex((prev) => (prev === 0 ? room.images.length - 1 : prev - 1));
  };

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-brand-100 overflow-hidden">
      <div className="grid lg:grid-cols-2 gap-0">
        {/* Left Column: Image & Thumbnails */}
        <div className="flex flex-col h-full bg-brand-50 border-b lg:border-b-0 lg:border-r border-brand-100">
          
          {/* Main Image Area - Grows to fill space */}
          <div className="relative flex-grow group overflow-hidden min-h-[300px] lg:min-h-0">
            <img 
              src={room.images[currentImageIndex]} 
              alt={room.name} 
              className="w-full h-full object-cover absolute inset-0 transition-opacity duration-300" 
            />
            
            {/* Navigation Arrows (appear on hover) */}
            <div className="absolute inset-0 flex items-center justify-between p-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
              <button 
                onClick={prevImage}
                className="pointer-events-auto bg-white/90 hover:bg-white text-brand-900 p-3 rounded-full shadow-lg transition-transform hover:scale-110 focus:outline-none"
                aria-label="Previous image"
              >
                <SafeIcon icon={FiChevronLeft} className="text-xl" />
              </button>
              <button 
                onClick={nextImage}
                className="pointer-events-auto bg-white/90 hover:bg-white text-brand-900 p-3 rounded-full shadow-lg transition-transform hover:scale-110 focus:outline-none"
                aria-label="Next image"
              >
                <SafeIcon icon={FiChevronRight} className="text-xl" />
              </button>
            </div>
          </div>

          {/* Thumbnails Bar - Positioned Below */}
          <div className="p-4 bg-white border-t border-brand-100">
            <div className="flex gap-3 justify-center lg:justify-start overflow-x-auto scrollbar-hide py-1">
              {room.images.map((img, idx) => (
                <button
                  key={idx}
                  onClick={() => setCurrentImageIndex(idx)}
                  className={`relative w-20 h-14 flex-shrink-0 rounded-md overflow-hidden transition-all duration-300 border-2 ${
                    currentImageIndex === idx 
                      ? 'border-brand-500 ring-2 ring-brand-200 ring-offset-1' 
                      : 'border-transparent opacity-70 hover:opacity-100 hover:border-brand-200'
                  }`}
                >
                  <img 
                    src={img} 
                    alt={`Thumbnail ${idx + 1}`} 
                    className="w-full h-full object-cover" 
                  />
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Right Column: Content */}
        <div className="p-8 md:p-12 flex flex-col justify-center">
          <h2 className="text-3xl font-serif text-brand-900 mb-6">{room.name}</h2>
          
          {/* Key Features Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8 border-b border-brand-100 pb-8">
            <div className="flex flex-col items-center text-center p-3 bg-brand-50 rounded-lg">
              <SafeIcon icon={FaRulerCombined} className="text-brand-500 mb-2" />
              <span className="text-xs font-bold uppercase text-brand-800">{room.size}</span>
            </div>
            <div className="flex flex-col items-center text-center p-3 bg-brand-50 rounded-lg">
              <SafeIcon icon={FaBed} className="text-brand-500 mb-2" />
              <span className="text-xs font-bold uppercase text-brand-800">{room.bed}</span>
            </div>
            <div className="flex flex-col items-center text-center p-3 bg-brand-50 rounded-lg">
              <SafeIcon icon={FaMountain} className="text-brand-500 mb-2" />
              <span className="text-xs font-bold uppercase text-brand-800">{room.view}</span>
            </div>
            <div className="flex flex-col items-center text-center p-3 bg-brand-50 rounded-lg">
              <SafeIcon icon={FaSmokingBan} className="text-brand-500 mb-2" />
              <span className="text-xs font-bold uppercase text-brand-800">{room.smoking}</span>
            </div>
          </div>

          <p className="text-brand-600 mb-8 leading-relaxed whitespace-pre-line">
            {room.description}
          </p>

          <div className="grid md:grid-cols-2 gap-8 mb-10">
            <div>
              <h3 className="font-serif text-lg text-brand-800 mb-4 flex items-center gap-2">
                <SafeIcon icon={FaBath} className="text-brand-500" />
                Private Bathroom
              </h3>
              <ul className="space-y-2">
                {room.bathroomAmenities.map((item, idx) => (
                  <li key={idx} className="text-sm text-brand-600 flex items-start gap-2">
                    <FiCheck className="mt-1 text-brand-400 shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h3 className="font-serif text-lg text-brand-800 mb-4 flex items-center gap-2">
                <SafeIcon icon={FaConciergeBell} className="text-brand-500" />
                Room Amenities
              </h3>
              <ul className="space-y-2">
                {room.roomAmenities.map((item, idx) => (
                  <li key={idx} className="text-sm text-brand-600 flex items-start gap-2">
                    <FiCheck className="mt-1 text-brand-400 shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 mt-auto">
            <Button to="/contact" variant="primary" className="flex-1 text-center justify-center">
              Check Availability
            </Button>
            <div className="text-xs text-brand-400 flex items-center justify-center sm:justify-start">
              * Best rates when booking direct
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const Rooms = () => {
  const rooms = [
    {
      id: 'double-room-private-bath',
      name: 'Double Room with Private Bathroom',
      size: '17 m²',
      bed: '1 double bed',
      view: 'Garden view',
      smoking: 'Non-smoking',
      description: `This air-conditioned double room features a private bathroom and free Wi-Fi. The room is equipped with a wardrobe, a desk, and tea and coffee making facilities, and offers relaxing garden views. The private bathroom includes a shower or bathtub, a hairdryer, slippers, bathrobes, and complimentary toiletries. Towels, bed linen, and toilet paper are provided.`,
      images: [
        "https://canv.wasacon.com/amani-resort-zanzibar/_assets/media/5b65541f83e4e10d4084cbafc929bf5d.jpg",
        "https://canv.wasacon.com/amani-resort-zanzibar/_assets/media/f421ff240e152c8e5c5782bc936daa14.jpg",
        "https://canv.wasacon.com/amani-resort-zanzibar/_assets/media/5e9655cae82a71eefe9467183f8e931c.jpg",
        "https://canv.wasacon.com/amani-resort-zanzibar/_assets/media/d460506f701e074c786e97263dbec448.jpg"
      ],
      bathroomAmenities: [
        "Free toiletries",
        "Bathrobe",
        "Toilet",
        "Bathtub or shower",
        "Towels",
        "Slippers",
        "Hairdryer",
        "Toilet paper"
      ],
      roomAmenities: [
        "Air conditioning (individual unit)",
        "Safe",
        "Bed linen",
        "Socket near the bed",
        "Desk",
        "Mosquito net",
        "Tea and coffee making facilities",
        "Fan",
        "Electric kettle",
        "Wardrobe"
      ]
    }
  ];

  return (
    <div className="page-transition pt-36 pb-20">
      <div className="container mx-auto px-6">
        <div className="text-center mb-16">
          <h1 className="text-4xl md:text-5xl mb-6">Our Rooms</h1>
          <p className="text-lg text-brand-600 max-w-2xl mx-auto">
            Relax in comfortable, air-conditioned rooms with peaceful garden views.
          </p>
        </div>

        <div className="space-y-24">
          {rooms.map((room) => (
            <RoomCard key={room.id} room={room} />
          ))}
        </div>

        {/* General Info Box */}
        <div className="mt-16 bg-brand-50 p-8 rounded-xl border border-brand-200 text-center max-w-3xl mx-auto">
          <h3 className="font-serif text-xl mb-4 text-brand-900">Important Information</h3>
          <p className="text-brand-700 mb-4">
            Check-in: 12:00 - 17:00 | Check-out: until 12:00
          </p>
          <ul className="inline-flex flex-wrap justify-center gap-6 text-sm text-brand-600">
            <li className="flex items-center gap-2"><FiWifi /> Free Wi-Fi in all rooms</li>
            <li className="flex items-center gap-2"><FaSnowflake /> Air Conditioning</li>
            <li className="flex items-center gap-2"><FaSmokingBan /> Non-smoking Rooms</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default Rooms;